/**
 * SoccerStrategy - Detection strategy for soccer ball juggle counting
 *
 * Uses YOLO object detection + XGBoost ML model for juggle counting.
 * Ball-only detection (no pose/keypoints) for better performance.
 * Draws bounding boxes for ball detection.
 * Uses 10fps setInterval loop with synthetic timestamps for replay parity.
 */
import { BaseExerciseStrategy } from './BaseExerciseStrategy.js';
import { registerStrategy } from './exerciseStrategyRegistry.js';

export class SoccerStrategy extends BaseExerciseStrategy {
  static get id() {
    return 'soccer';
  }

  static get displayName() {
    return 'Soccer';
  }

  static get detectorType() {
    return 'YOLO';
  }

  static get counterId() {
    return 'soccer';
  }

  static get audioPreset() {
    return 'skipping'; // Use same audio as skipping for now
  }

  getLabels() {
    return {
      title: 'Soccer Ball Juggle Counter',
      repNoun: 'Juggles',
      cameraTip: 'Position camera so ball and your feet are visible.',
      beepMessage: 'Every time I juggle the ball correctly the beep sound will play',
      loadingModel: 'Loading YOLO + XGBoost models...',
      ball: 'Ball',
      detected: 'Detected',
      notDetected: 'Not detected'
    };
  }

  getInitialStatus() {
    return { ball: false };
  }

  getInitialDebugState() {
    return {
      // XGBoost ML model status
      predictions: 0,
      lastProb: 0,
      bufferSize: 0,
      isModelLoaded: false,
      isLoading: true,
      loadError: null,
      // YOLO detector status
      yoloReady: false,
      yoloLoading: true,
      yoloError: null
    };
  }

  /**
   * Run YOLO ball detection
   * Returns {detections} for use in buildFrameData and drawFrame
   */
  async runDetection(video) {
    const detections = await this.detect(video);
    return { detections };
  }

  /**
   * Build frame data from YOLO detections (ball only)
   */
  buildFrameData(rawDetections, timestamp) {
    const { detections } = rawDetections || {};
    const ballDet = detections?.find(d => d.class === 'ball');

    return {
      timestamp,
      ball: ballDet ? {
        x: ballDet.bbox.x,
        y: ballDet.bbox.y,
        width: ballDet.bbox.width,
        height: ballDet.bbox.height,
        confidence: ballDet.confidence
      } : null
    };
  }

  /**
   * Extract status from frame data
   */
  getStatusFromFrameData(frameData) {
    return {
      ball: !!frameData?.ball
    };
  }

  /**
   * Build debug state from counter state
   */
  buildDebugState(counterState) {
    return {
      predictions: counterState.predictionsThisSession || 0,
      lastProb: counterState.lastProbability || 0,
      bufferSize: counterState.bufferSize || 0,
      isModelLoaded: counterState.isModelLoaded,
      loadError: counterState.loadError
    };
  }

  /**
   * Draw ball detections
   */
  drawFrame(ctx, video, rawDetections, frameData, currentRepCount) {
    const { detections } = rawDetections || {};

    if (detections) {
      for (const det of detections) {
        const { bbox, class: className, confidence } = det;
        ctx.strokeStyle = '#00aaff'; // Cyan for ball (soccer color)
        ctx.lineWidth = 3;
        ctx.strokeRect(bbox.x, bbox.y, bbox.width, bbox.height);

        ctx.fillStyle = ctx.strokeStyle;
        ctx.font = '16px Arial';
        ctx.fillText(`${className} ${(confidence * 100).toFixed(0)}%`, bbox.x, bbox.y - 5);
      }
    }

    // Draw rep count (readable on mirrored canvas)
    this.drawReadableText(ctx, `${currentRepCount}`, 20, 60, {
      fillStyle: '#fff',
      strokeStyle: '#000',
      lineWidth: 3,
      font: 'bold 48px Arial'
    });
  }

  convertToFrameData(detections, timestamp) {
    const ballDet = detections?.find(d => d.class === 'ball');

    return {
      timestamp,
      ball: ballDet ? {
        x: ballDet.bbox.x,
        y: ballDet.bbox.y,
        width: ballDet.bbox.width,
        height: ballDet.bbox.height,
        confidence: ballDet.confidence
      } : null
    };
  }

  renderStatusDisplay(status, labels) {
    const l = labels || this.getLabels();

    return (
      <div style={{ display: 'flex', gap: '15px', marginTop: '10px', fontSize: '14px' }}>
        <span style={{ color: status.ball ? '#4CAF50' : '#f44336' }}>
          {l.ball}: {status.ball ? l.detected : l.notDetected}
        </span>
      </div>
    );
  }

  getModelVersionsDisplay(modelVersions) {
    // Ball detector - prefer modelName, fallback to extracting hash from filename
    let ballVersion = modelVersions?.yolo?.modelName;
    if (!ballVersion) {
      // Fallback: extract hash from model-HASH.json filename
      ballVersion = modelVersions?.yolo?.modelJson?.replace('model-', '').replace('.json', '') || '?';
      if (ballVersion === 'model') ballVersion = '?'; // model.json has no hash
    }
    // XGBoost model hash
    let xgboostDigest = modelVersions?.xgboost?.xgboostModel?.replace('.json', '').replace('xgboost-juggle-model-', '').replace('xgboost-juggle-model', '') || '?';
    if (xgboostDigest === '') xgboostDigest = 'default';
    return `Engine: ${this.engineDisplayLabel()} - YOLO: ${ballVersion} - XGBoost: ${xgboostDigest}`;
  }

  renderDebugPanel(debugState) {
    return null;
  }

  supportsRecording() {
    return true;
  }

  getMaxRecordingFrames() {
    return 5000;
  }

  getRecordingFPS() {
    return 10;
  }

  initializeMovementLog(maxFrames) {
    // Pre-allocate array for YOLO (fixed-size buffer)
    return new Array(maxFrames);
  }

  sliceMovementLog(log, frameCount) {
    return log.slice(0, frameCount || log.length);
  }
}

// Register this strategy
registerStrategy(SoccerStrategy);
